"use client";

import { useTranslation } from "react-i18next";
import { AssetsDirectory, SignupInputFieldName, TranslationScopes } from "@finbackoffice/enums";
import classNames from "classnames";
import {
    FC,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { defaultSelfExclusionPeriods, RequestError } from "@finbackoffice/fe-core";
import { InferType } from "yup";
import Button from "components/base/button/Button";
import Translate from "components/base/translate/Translate";
import SiteNotification from "components/base/notification/SiteNotification";
import {
    IUpdateSelfExclusionV2,
    useDate,
    useNotification,
    useResponsibleGamingV2,
    useSignupConfigV2,
} from "hooks";
import Input from "components/base/input-field/Input";
import { Svg } from "components/base/svg/Svg";
import UniversalDeviceDropdown from "components/base/universal-device-dropdown/UniversalDeviceDropdown";
import { selfExclusionValidationSchema, DropdownOption } from "@finbackoffice/site-core";
import { UserAccountContextV2, IModalForwardRefPropsV2 } from "contexts";
import AccountDetailsFormBlock from "../form-block/AccountDetailsFormBlock";
import ConfirmationModal from "./ConfirmationModal";
import styles from "./responsible-gaming.module.sass";

export type ISelfExclusionForm = InferType<typeof selfExclusionValidationSchema>;

const SelfExclusion: FC = () => {
    const { updateUser } = useContext(UserAccountContextV2);
    const { formatDistanceToNowStrict, addDays, formatDate, differenceInYears } = useDate();
    const { t } = useTranslation(TranslationScopes.UserAccount);
    const { t: commonT } = useTranslation();
    const { signupConfigObj } = useSignupConfigV2([SignupInputFieldName.Password]);
    const [showPassword, setShowPassword] = useState(false);
    const [payload, setPayload] = useState<IUpdateSelfExclusionV2 | null>(null);
    const [loading, setLoading] = useState(false);
    const { setNotification, notification } = useNotification();
    const modalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);
    const { updateSelfExclusion } = useResponsibleGamingV2();
    const {
        control,
        handleSubmit,
        formState: { isSubmitting, isValid, errors, isDirty, dirtyFields },
    } = useForm({
        mode: "onChange",
        context: { configObj: { ...signupConfigObj, t: commonT } },
        resolver: yupResolver(selfExclusionValidationSchema),
        defaultValues: { period: "", password: "" },
    });

    const isPermanent = useMemo(
        () => (payload?.period ? differenceInYears(payload.period, new Date()) >= 100 : false),
        [differenceInYears, payload?.period],
    );

    useEffect(() => {
        if (payload) {
            setNotification({ type: "", error: "", message: "" });
            modalRef.current?.open();
        }
    }, [payload, setNotification]);

    const onSubmit = useCallback(
        (data: ISelfExclusionForm) => {
            if (isDirty && isValid && !isSubmitting) {
                let changedFields: ISelfExclusionForm = {} as ISelfExclusionForm;
                Object.keys(dirtyFields).forEach((field) => {
                    changedFields = {
                        ...changedFields,
                        [field]: data[field as keyof ISelfExclusionForm],
                    };
                });

                setPayload(changedFields as IUpdateSelfExclusionV2);
            }
        },
        [dirtyFields, isDirty, isSubmitting, isValid],
    );

    const exclusionFormSubmit = useCallback(async () => {
        setLoading(true);
        try {
            const response = await updateSelfExclusion(payload as IUpdateSelfExclusionV2);
            updateUser(response);
            await modalRef.current?.close();
        } catch (err: any) {
            const error: RequestError = err.response?.data;
            if (error.error) {
                setNotification({
                    type: "error",
                    error: error.error,
                    message: error.message,
                });
            }
            setLoading(false);
        }
    }, [updateSelfExclusion, payload, updateUser, setNotification]);

    const dropDownOptions = useMemo(() => {
        const result: DropdownOption[] = [];
        const periods = defaultSelfExclusionPeriods;

        periods.forEach((periodInDays: number) => {
            const value = addDays(new Date(), periodInDays);

            result.push({
                value: value.toISOString(),
                label:
                    periodInDays === 36600
                        ? t("account_responsible_gaming_period_permanent")
                        : formatDistanceToNowStrict(value),
            });
        });

        return result;
    }, [addDays, formatDistanceToNowStrict, t]);

    const renderLabel = useCallback(
        () => (
            <>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-self-exclusion.svg`}
                    className="svg-icon"
                />

                <Translate
                    tid="account_responsible_gaming_self_exclusion"
                    namespace={TranslationScopes.UserAccount}
                />
            </>
        ),
        [],
    );

    return (
        <AccountDetailsFormBlock renderHeader={renderLabel}>
            <div className={styles.compact}>
                <div>
                    <form
                        className={styles.form}
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit)}>
                        <p>
                            <Translate
                                tid="account_responsible_gaming_self_exclusion_text"
                                namespace={TranslationScopes.UserAccount}
                            />
                        </p>

                        <Controller
                            render={({ field: { onChange, value, name } }) => (
                                <UniversalDeviceDropdown
                                    testId="exclusion-period"
                                    name={name}
                                    onChange={onChange}
                                    value={value || ""}
                                    label={t("account_responsible_gaming_period")}
                                    placeholder={t("account_responsible_gaming_period_placeholder")}
                                    options={dropDownOptions}
                                    error={errors.period}
                                />
                            )}
                            control={control}
                            name="period"
                        />

                        <Controller
                            render={({ field: { onChange, value, name } }) => {
                                return (
                                    <Input
                                        onChange={onChange}
                                        value={value}
                                        name={name}
                                        data-testid="exclusion-password"
                                        label={commonT("userData_password")}
                                        innerIconClass={classNames(
                                            "viewPass",
                                            showPassword && "showPass",
                                        )}
                                        type={showPassword ? "text" : "password"}
                                        onInnerIconClick={() => setShowPassword(!showPassword)}
                                        error={errors.password}
                                    />
                                );
                            }}
                            control={control}
                            name="password"
                        />
                        <section className={styles.btnContainer}>
                            <Button
                                type="submit"
                                data-testid="confirm-exclusion-button"
                                disabled={!isDirty || !isValid || isSubmitting}
                                variant="primary">
                                <Translate
                                    tid="account_responsible_gaming_confirm"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </Button>
                        </section>
                    </form>
                </div>
            </div>
            {payload && (
                <ConfirmationModal
                    ref={modalRef}
                    loading={loading}
                    error={
                        notification.type && (
                            <SiteNotification
                                type={notification.type}
                                title={notification.error}
                                message={notification.message}
                            />
                        )
                    }
                    text={
                        isPermanent
                            ? t("account_responsible_gaming_permanent_exclusion_modal_text")
                            : t("account_responsible_gaming_till_exclusion_modal_text", {
                                  replace: {
                                      period: payload.period
                                          ? formatDate(
                                                new Date(payload.period),
                                                "dd MMM yyyy HH:mm",
                                                false,
                                            )
                                          : "",
                                  },
                              })
                    }
                    onConfirm={exclusionFormSubmit}
                    handleClose={() => {
                        void modalRef.current?.close();
                    }}
                />
            )}
        </AccountDetailsFormBlock>
    );
};

export default SelfExclusion;
